import { useState, useEffect } from 'react';
import { YStack, H1, H2, Paragraph, ScrollView, Card } from 'tamagui';
import { useAuthContext } from '../../auth';
import { isAdmin } from '../../auth/accessControl';
import { Button } from '../../components';

export const AdminDebug = () => {
  const { user } = useAuthContext();
  const [adminStatus, setAdminStatus] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<string[]>([]);

  useEffect(() => {
    const checkAdmin = async () => {
      setLoading(true);
      setLogs([]);
      
      addLog('🔍 Starting admin check...');
      
      if (!user) {
        addLog('❌ No user logged in');
        setAdminStatus(false);
        setLoading(false);
        return;
      }

      addLog(`✓ User: ${user.email}`);
      addLog(`✓ User ID: ${user.id}`);
      addLog(`✓ User Role: ${user.role}`);
      addLog(`✓ User Name: ${user.name || 'N/A'}`);
      
      try {
        addLog('📞 Calling access.is_admin() RPC function...');
        const isAdminResult = await isAdmin();
        addLog(`✓ RPC returned: ${isAdminResult}`);
        setAdminStatus(isAdminResult);
        
        if (isAdminResult) {
          addLog('✅ USER IS ADMIN!');
        } else {
          addLog('⚠️ User is NOT admin');
          addLog('To make user admin, click "Make Admin" button on /profile');
        }
      } catch (err) {
        addLog(`❌ Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setAdminStatus(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdmin();
  }, [user]);

  const addLog = (message: string) => {
    setLogs((prev) => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const handleCheckAgain = async () => {
    setLoading(true);
    setLogs([]);
    const result = await isAdmin();
    setAdminStatus(result);
    addLog(`✓ Check completed. Admin: ${result}`);
    setLoading(false);
  };

  return (
    <ScrollView flex={1} backgroundColor="$gray2">
      <YStack
        flex={1}
        padding="$4"
        minHeight="100vh"
        paddingBottom="$8"
      >
        <YStack maxWidth={800} alignSelf="center" width="100%" gap="$6">
          {/* Header */}
          <YStack gap="$2">
            <H1 fontSize="$9" fontWeight="700" color="$gray12">
              🔐 Admin Debug Console
            </H1>
            <Paragraph fontSize="$4" color="$gray11">
              Check if you're an admin and debug access control
            </Paragraph>
          </YStack>

          {/* Status Card */}
          <Card
            elevate
            padded
            backgroundColor={
              adminStatus === true
                ? '$green2'
                : adminStatus === false
                  ? '$red2'
                  : '$blue2'
            }
          >
            <YStack gap="$3">
              <H2
                fontSize="$6"
                fontWeight="600"
                color={
                  adminStatus === true
                    ? '$green11'
                    : adminStatus === false
                      ? '$red11'
                      : '$blue11'
                }
              >
                {loading ? '⏳ Checking...' : adminStatus === true ? '✅ ADMIN' : '❌ NOT ADMIN'}
              </H2>
              {adminStatus !== null && (
                <Paragraph
                  fontSize="$4"
                  color={
                    adminStatus === true
                      ? '$green11'
                      : adminStatus === false
                        ? '$red11'
                        : '$blue11'
                  }
                >
                  {adminStatus === true
                    ? 'You have admin privileges!'
                    : adminStatus === false
                      ? 'You are not an admin. Click "Make Admin" on your profile page.'
                      : 'Checking admin status...'}
                </Paragraph>
              )}
            </YStack>
          </Card>

          {/* User Info Card */}
          {user && (
            <Card elevate padded>
              <YStack gap="$3">
                <H2 fontSize="$6" fontWeight="600" color="$gray12">
                  Current User
                </H2>
                <YStack gap="$2">
                  <Paragraph fontSize="$3" color="$gray12">
                    <Paragraph fontWeight="600">Email:</Paragraph> {user.email}
                  </Paragraph>
                  <Paragraph fontSize="$3" color="$gray12">
                    <Paragraph fontWeight="600">ID:</Paragraph> {user.id}
                  </Paragraph>
                  <Paragraph fontSize="$3" color="$gray12">
                    <Paragraph fontWeight="600">Role:</Paragraph> {user.role}
                  </Paragraph>
                  <Paragraph fontSize="$3" color="$gray12">
                    <Paragraph fontWeight="600">Name:</Paragraph> {user.name || 'Not set'}
                  </Paragraph>
                </YStack>
              </YStack>
            </Card>
          )}

          {/* Logs Card */}
          <Card elevate padded backgroundColor="$gray1">
            <YStack gap="$3">
              <H2 fontSize="$6" fontWeight="600" color="$gray12">
                📋 Debug Logs
              </H2>
              <YStack
                gap="$1"
                backgroundColor="$gray2"
                padding="$3"
                borderRadius="$2"
                maxHeight={300}
                overflowY="auto"
              >
                {logs.length === 0 ? (
                  <Paragraph fontSize="$3" color="$gray10" italic>
                    No logs yet...
                  </Paragraph>
                ) : (
                  logs.map((log, idx) => (
                    <Paragraph key={idx} fontSize="$2" color="$gray11" fontFamily="monospace">
                      {log}
                    </Paragraph>
                  ))
                )}
              </YStack>
            </YStack>
          </Card>

          {/* Action Button */}
          <Button
            label={loading ? 'Checking...' : 'Check Admin Status Again'}
            variant="primary"
            onPress={handleCheckAgain}
            disabled={loading}
          />

          {/* Instructions Card */}
          <Card elevate padded backgroundColor="$blue2">
            <YStack gap="$2">
              <H2 fontSize="$5" fontWeight="600" color="$blue11">
                ℹ️ How to become admin:
              </H2>
              <Paragraph fontSize="$3" color="$blue11">
                1. Go to <Paragraph fontWeight="600">/profile</Paragraph>
              </Paragraph>
              <Paragraph fontSize="$3" color="$blue11">
                2. Click <Paragraph fontWeight="600">"🔐 Make Admin"</Paragraph> button
              </Paragraph>
              <Paragraph fontSize="$3" color="$blue11">
                3. Refresh this page to verify
              </Paragraph>
            </YStack>
          </Card>
        </YStack>
      </YStack>
    </ScrollView>
  );
};
